import * as api from '../../../api';
import { pagingDataUtilities } from '../../../common';

export const fetchSiteListActions = api.fetchActions('site-list', 'fetch-site-list');
export const fetchProgramsBySiteListActions = api.fetchActions('site-list', 'fetch-programs-by-site');
export const pagingActions = pagingDataUtilities.pagingActions('site-list', 'paging');
export const fetchCompleteSiteListActions = api.fetchActions('site-list', 'fetch-complete-site-list');

export const actionTypes = {
  ...fetchSiteListActions.actionTypes,
  ...pagingActions.actionTypes,
  ...fetchProgramsBySiteListActions.actionTypes,
  ...fetchCompleteSiteListActions.actionTypes,
  ADD_ALL_SITES_TO_LIST: 'site-list.add-sites-to-list',
  CHANGE_SELECTED_SITE: 'site-list.change-selected-site',
  SET_SELECTED_SITE : 'site-list.set-selected-site',
  SET_SELECTED_PROGRAM : 'site-list.set-selected-program',
  CHANGE_SELECTED_PROGRAM: 'site-list.change-selected-program',
  CLEAR_SELECTED_SITE_PROGRAM: 'site-list.clear-selected-site-program'
}

//The number of sites to return for each page call
const pageSize = 50;
const pageParams = pageNumber => ({
  page: pageNumber,
  perPage: pageSize,
  sortBy: "name",
  sortAsc: true
});

/**
 * Retrieves a single page of sites from the API
 */
export function fetchSiteList() {
  return (dispatch, getState) => {
    const state = getState();
    const { sites } = state;
    const { pagingData } = sites;
    const { currentPage, itemsPerPage, sortBy, sortAsc } = pagingData || {};
    const params = {
      page: currentPage,
      perPage: itemsPerPage,
      sortBy,
      sortAsc
    };
    return dispatch(api.shouldExecute(['sites', 'siteListState'], api.getThunk(`/sites`, params, fetchSiteListActions)));
  }
}

export function addSitesToList(sites, sortName, sortAsc) {
  return {
    type: actionTypes.ADD_ALL_SITES_TO_LIST,
    data: {
      totalPages: 1,
      currentPage: 1,
      totalResults: sites.length,
      sort: sortName,
      sortAscending: sortAsc,
      results: sites
    }
  }
}

/**
 * Retrieves multiple site pages from the API until all sites are loaded
 * @param {object} dispatch The dispatcher that handles calling the actions.
 * @param {object} primaryResults The first page of sites that has needed metadata.
 */
function getAllPages(dispatch, primaryResults) {
  const totalResults = primaryResults.totalResults;
  const totalPages = Math.ceil(totalResults / pageSize);

  var pagePromises = [];
  pagePromises.push(new Promise((resolve, reject) => {
    resolve(primaryResults);
  }));

  for(var i = 2; i <= totalPages; i++) {
    pagePromises.push(dispatch(api.getThunk(`/sites`, pageParams(i), fetchSiteListActions)));
  }

  return Promise.all(pagePromises);
}

/**
 * Takes all site pages that were returned and flattens them into a single array
 * @param {object} allSites A map that contains the responses of all site pages.
 */
function flattenSitePages(allSites) {
  var allPages = [];
  allSites.forEach((result) => {
    allPages = allPages.concat(result.results);
  });
  return allPages;
}

/**
 * Function that retrieves and stores all sites:
 * 1) The first site page is requested.
 * 2) The function continues to call the next pages (if needed) until all pages have
 *    been returned.
 * 3) All paged site data is flattened and stored
 */
export function fetchCompleteSiteList() {
  return (dispatch, getState) => {
    dispatch({ type: actionTypes.FETCH_COMPLETE_SITE_LIST_BEGIN });

    dispatch(api.getThunk(`/sites`, pageParams(1), fetchSiteListActions))
    .then((primaryResults) => {
      return getAllPages(dispatch, primaryResults);
    })
    .then((siteResults) => {
      return flattenSitePages(siteResults);
    })
    .then((allSites) => {
      dispatch(addSitesToList(allSites, "name", true));
    })
    .then(() => {
      dispatch({ type: actionTypes.FETCH_COMPLETE_SITE_LIST_SUCCESS });
    })
    .catch((err) => {

    })
  }
}

/**
 * Function that retrieves a list of sites that have ATLEAST one program enabled
 * 1) The first site page is requested.
 * 2) The function continues to call the next pages (if needed) until all pages have
 *    been returned.
 * 3) All paged site data is flattened and stored
 */
export function fetchSiteProgramEnabledList() {
  return (dispatch, getState) => {
    dispatch({ type: actionTypes.FETCH_COMPLETE_SITE_LIST_BEGIN });

    dispatch(api.getThunk(`/sites/GetAllSites`, pageParams(1), fetchSiteListActions))
    .then((primaryResults) => {
      return getAllPages(dispatch, primaryResults);
    })
    .then((siteResults) => {
      return flattenSitePages(siteResults);
    })
    .then((allSites) => {
      dispatch(addSitesToList(allSites, "name", true));
    })
    .then(() => {
      dispatch({ type: actionTypes.FETCH_COMPLETE_SITE_LIST_SUCCESS });
    })
    .catch((err) => {

    })
  }
}

export const changePageNumber = (page) => pagingDataUtilities.changePage(page, pagingActions);
export const changeSort = (newSort) => pagingDataUtilities.changeSort(newSort, pagingActions, ['sites', 'pagingData']);

export function changeSelectedSite(siteId) {
  return {
    type: actionTypes.CHANGE_SELECTED_SITE,
    siteId
  }
}

export function changeSelectedProgram(programId) {
  return {
    type: actionTypes.CHANGE_SELECTED_PROGRAM,
    programId
  }
}

export function fetchProgramsForSite(siteId) {
  return (dispatch, getState) => {
    return dispatch(api.getThunk(`/sites/${siteId}/programs/all`, { siteId }, fetchProgramsBySiteListActions, { siteId }));
  }
}

/**
 * This action method is called an admin selects a site to search for registered/unregistered participants. It updates the store with
 * the selected site from the dropdownlist
 * @param {guid} siteId
 */
export function setSelectedSite(siteId) {
  return {
    type : actionTypes.SET_SELECTED_SITE,
    siteId
  }
}

/**
 * This action method is called an admin selects a program to search for registered/unregistered participants. It updates the store with
 * the selected program from the dropdownlist
 * @param {guid} programId
 */
export function setSelectedProgram(programId) {
  return {
    type : actionTypes.SET_SELECTED_PROGRAM,
    programId
  }
}

/**
 * This action method is called when the admin goes back to searching for participants using lastname/email. It clears the store if there
 * was a currently selected site or program
 */
export function clearSiteProgramIds() {
  return {
    type: actionTypes.CLEAR_SELECTED_SITE_PROGRAM
  }
}
